import { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ArrowLeft, Upload, FileText, Loader2, Check, X, Settings } from 'lucide-react';
import { API } from '@/App';
import { toast } from 'sonner';
import Sidebar from '@/components/Sidebar';

const TemplateBuilder = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [file, setFile] = useState(null);
  const [categories, setCategories] = useState([]);
  const [templateData, setTemplateData] = useState({
    name: '',
    description: '',
    is_public: false,
    category: '',
    tags: []
  });

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/categories`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCategories(data);
      }
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (!selectedFile.name.endsWith('.docx')) {
        toast.error('Please select a .docx file');
        return;
      }
      setFile(selectedFile);
      if (!templateData.name) {
        setTemplateData(prev => ({
          ...prev,
          name: selectedFile.name.replace('.docx', '')
        }));
      }
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error('Please select a file');
      return;
    }

    if (!templateData.name) {
      toast.error('Please enter a template name');
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('template_data', JSON.stringify(templateData));

      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/templates/upload`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to upload template');
      }

      const template = await response.json();
      toast.success('Template created successfully!');
      navigate(`/templates/${template.template_id}/edit`);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Navigation */}
      <nav className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center h-16">
            <Button
              variant="ghost"
              onClick={() => navigate('/templates')}
              className="rounded-xl"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Templates
            </Button>
          </div>
        </div>
      </nav>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-6 md:px-12 py-12">
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2">Create New Template</h1>
          <p className="text-lg text-slate-600">
            Upload a Word document and we'll extract fields for you
          </p>
        </div>

        <div className="space-y-6">
          {/* File Upload */}
          <div className="p-8 rounded-2xl bg-white border border-slate-200 shadow-lg">
            <h2 className="text-xl font-bold mb-4">Upload Document</h2>
            
            <div
              onClick={() => fileInputRef.current?.click()}
              className={`border-2 border-dashed rounded-2xl p-12 text-center cursor-pointer transition-all duration-300 ${
                file
                  ? 'border-violet-500 bg-violet-50'
                  : 'border-slate-300 hover:border-violet-500 hover:bg-slate-50'
              }`}
              data-testid="file-upload-area"
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".docx"
                onChange={handleFileSelect}
                className="hidden"
                data-testid="file-input"
              />
              
              {file ? (
                <div className="flex flex-col items-center">
                  <div className="w-16 h-16 rounded-full bg-violet-100 flex items-center justify-center mb-4">
                    <Check className="w-8 h-8 text-violet-600" />
                  </div>
                  <p className="text-lg font-bold text-violet-600 mb-1">{file.name}</p>
                  <p className="text-sm text-slate-600 mb-4">
                    {(file.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      setFile(null);
                    }}
                    className="rounded-xl"
                  >
                    <X className="w-4 h-4 mr-2" />
                    Remove
                  </Button>
                </div>
              ) : (
                <div>
                  <Upload className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                  <p className="text-lg font-bold text-slate-700 mb-2">
                    Click to upload or drag and drop
                  </p>
                  <p className="text-sm text-slate-500">
                    Word documents (.docx) only
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Template Details */}
          <div className="p-8 rounded-2xl bg-white border border-slate-200 shadow-lg">
            <h2 className="text-xl font-bold mb-6">Template Details</h2>
            
            <div className="space-y-4">
              <div>
                <Label htmlFor="name" className="text-sm font-medium text-slate-700 mb-2">
                  Template Name *
                </Label>
                <Input
                  id="name"
                  value={templateData.name}
                  onChange={(e) => setTemplateData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Invoice Template"
                  className="h-12 rounded-xl border-2 border-slate-200 focus:border-violet-500"
                  data-testid="template-name-input"
                />
              </div>

              <div>
                <Label htmlFor="description" className="text-sm font-medium text-slate-700 mb-2">
                  Description
                </Label>
                <Textarea
                  id="description"
                  value={templateData.description}
                  onChange={(e) => setTemplateData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Describe what this template is for..."
                  rows={4}
                  className="rounded-xl border-2 border-slate-200 focus:border-violet-500 resize-none"
                  data-testid="template-description-input"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label htmlFor="category" className="text-sm font-medium text-slate-700">
                    Category
                  </Label>
                  <Link 
                    to="/categories" 
                    className="text-xs text-violet-600 hover:text-violet-700 flex items-center gap-1"
                  >
                    <Settings className="w-3 h-3" />
                    Manage
                  </Link>
                </div>
                <select
                  id="category"
                  value={templateData.category}
                  onChange={(e) => setTemplateData(prev => ({ ...prev, category: e.target.value }))}
                  className="w-full h-12 rounded-xl border-2 border-slate-200 focus:border-violet-500 px-4 bg-white"
                  data-testid="template-category-select"
                >
                  <option value="">Select a category</option>
                  {categories.map(cat => (
                    <option key={cat.category_id} value={cat.slug}>{cat.name}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-4">
            <Button
              variant="outline"
              onClick={() => navigate('/templates')}
              className="h-12 px-6 rounded-xl border-2"
              data-testid="cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpload}
              disabled={!file || !templateData.name || uploading}
              className="h-12 px-8 rounded-xl bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white font-bold disabled:opacity-50"
              data-testid="create-template-btn"
            >
              {uploading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <FileText className="w-5 h-5 mr-2" />
                  Create Template
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TemplateBuilder;